import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

import heroImage from '../../assets/images/hero.jpg';
import pay2Image from '../../assets/images/pay2.jpg';
import mockImage from '../../assets/images/mock.jpg';
import careers1Image from '../../assets/images/careers-1.png';
import './Careers.css';

const HeroSection = styled.section`
  position: relative;
  height: 80vh;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 30vh;
    min-height: 250px;
  }
`;

const Slide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(${props => props.image}) center center/cover no-repeat;
  opacity: ${props => props.active ? 1 : 0};
  transform: ${props => props.active ? 'scale(1.1)' : 'scale(1)'};
  transition: opacity 1s ease-in-out, transform 6s ease-in-out;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: left;
  color: white;
  margin-left: 5%;

  @media (max-width: 768px) {
    text-align: center;
    margin-left: 0;
    padding: 0 20px;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  margin: 0;
  line-height: 1.2;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background: #C31321;
  }

  @media (max-width: 768px) {
    font-size: 3rem;
  }

  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const CareersSection = styled.section`
  padding: 6rem 0;
  background: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
  margin-bottom: 3rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: #C31321;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
`;

const CareersContent = styled(motion.div)`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const CareersDescription = styled.p`
  font-size: 1.1rem;
  color: #64748b;
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const NewSection = styled.section`
  padding: 6rem 0;
  background: #f8fafc;
`;

const NewSectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
  margin-bottom: 4rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: #C31321;
  }
`;

const NewSectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const NewSectionContent = styled(motion.div)`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ImageWithRequirements = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const NewSectionImage = styled.div`
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }
  }
`;

const NewSectionText = styled.div`
  padding-left: 6rem;

  h3 {
    font-size: 3rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.5rem;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 0;
      width: 60px;
      height: 3px;
      background: #C31321;
    }
  }

  h4 {
    font-size: 1rem;
    font-weight: 400;
    color: #C31321;
    font-style: italic;
    margin-bottom: 1.5rem;
    margin-top: 0;
  }

  p {
    font-size: 0.95rem;
    color: #64748b;
    line-height: 1.7;
    margin-bottom: 1.5rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 1.5rem 0;
  }

  li {
    font-size: 0.9rem;
    color: #64748b;
    line-height: 1.6;
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
    position: relative;

    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: #C31321;
      font-weight: bold;
    }
  }

  @media (max-width: 768px) {
    padding-left: 0;

    h3 {
      font-size: 2.5rem;
    }
  }
`;

const RequirementsSection = styled.section`
  padding: 4rem 0;
  background: #f8fafc;
`;

const RequirementsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const RequirementsContent = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  text-align: left;
`;

const CallToAction = styled.p`
  font-size: 1.1rem;
  color: #C31321;
  font-style: italic;
  font-weight: 400;
  margin-bottom: 2rem;
`;

const RequirementsTitle = styled.h3`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background: #C31321;
  }
`;

const RequirementsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
  text-align: left;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const RequirementsItem = styled.li`
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 1rem;
  padding-left: 1.5rem;
  position: relative;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    top: 0;
    color: #C31321;
    font-weight: bold;
    font-size: 1.2rem;
  }
`;

const Careers = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [heroImage, pay2Image, mockImage];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <>
      <HeroSection>
        {slides.map((slide, index) => (
          <Slide
            key={index}
            image={slide}
            active={index === currentSlide}
          />
        ))}
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            KeyPlots Careers
          </HeroTitle>
        </HeroContent>
      </HeroSection>



      <NewSection>
        <NewSectionContainer>
          <NewSectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Parking Marshals
          </NewSectionTitle>
          <NewSectionContent
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <ImageWithRequirements>
              <NewSectionImage>
                <img src={careers1Image} alt="KeyPlot Careers Opportunity" />
              </NewSectionImage>
              
              <RequirementsContent>
                <CallToAction>
                  Your journey to financial freedom starts with just one step. APPLY TODAY!!
                </CallToAction>
                
                <RequirementsTitle>Requirements:</RequirementsTitle>
                
                <RequirementsList>
                  <RequirementsItem>Competency in writing and speaking English as second or first language</RequirementsItem>
                  <RequirementsItem>Strong numerical skills comprehensive summation and subtraction in</RequirementsItem>
                  <RequirementsItem>Target-driven mindset</RequirementsItem>
                  <RequirementsItem>Literate on smart phone and keyboard</RequirementsItem>
                </RequirementsList>
              </RequirementsContent>
            </ImageWithRequirements>
            
            <NewSectionText>
              <h3>BE YOUR <br />OWN BOSS</h3>
              <h4>BECOME AN INDEPENDENT PARKING MARSHAL "IPM"</h4>
              <p>
                Are you ready to be your own boss? Trade your free time for extra income! 
                As an Independent Parking Marshal IPM, YOU decide how much you earn by managing 
                your time and performance!
              </p>
              
              <h4>Why Join?</h4>
              <ul>
                <li>Flexibility: Create your own schedule!</li>
                <li>Sky is your limit to make more money, Earnings is in Your Hands: Boost your income with a little investment commission-based structure.</li>
                <li>Independence, freedom: Work on your terms.</li>
              </ul>
              
              <h4>Application Procedure</h4>
              <ul>
                <li>Apply today and secure your position, life time opportunity.</li>
                <li>Send your updated CV and certified Qualifications to recruitment@united.com.na</li>
                <li>We shall invite you for a personal assessment.</li>
              </ul>
              
              <p><strong>CLOSING DATE: 23 AUGUST 2024</strong></p>
            </NewSectionText>
          </NewSectionContent>
        </NewSectionContainer>
      </NewSection>
    </>
  );
};

export default Careers; 